import Button from "@/components/ui/button";
import CustomInput from "@/components/ui/input";
import { authApi } from "@/core/auth/services/auth.api";
import { setUserFromToken } from "@/store";
import { t } from "i18next";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/login.module.scss";
import Email from "@/components/icons/email";
import { Lock } from "@/components/icons";
import useNotification from "@/hooks/use-notification";

interface AdminLoginProps {
  currentDomainID: string;
  currentDomainName: string;
  onForgotPassword?: () => void;
  /** Varsa parent user type doğrulaması yapacak - setUserFromToken/navigate parent'ta */
  onLoginSuccess?: (response: any) => void;
}

type FieldErrors = Partial<Record<"username" | "password", string>>;

const AdminLogin: React.FC<AdminLoginProps> = ({
  currentDomainID,
  currentDomainName,
  // onForgotPassword,
  onLoginSuccess,
}) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    password: "",
    username: "",
  });
  const { notifyError, notifySuccess } = useNotification();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [, setDomainID] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentDomainID) {
      setDomainID(currentDomainID);
    }
  }, [currentDomainID, currentDomainName]);

  const formFields = [
    {
      label: t("login.username"),
      name: "username",
      placeholder: t("login.usernamePlaceholder"),
      type: "text",
      maxLength: 255,
      variant: "standard" as const,
      startIcon: <Email width={24} height={24} />,
    },
    {
      label: t("login.password"),
      name: "password",
      placeholder: "**********",
      type: "password",
      maxLength: 512,
      variant: "standard" as const,
      startIcon: <Lock width={24} height={24} />,
    },
  ];

  const handleInputChange =
    (field: "username" | "password") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      setForm((prevForm) => ({
        ...prevForm,
        [field]: e.target.value,
      }));
    };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const usernameEmpty = !form.username.trim();
    const passwordEmpty = !form.password.trim();
    if (usernameEmpty || passwordEmpty) {
      const errors: FieldErrors = {};
      if (usernameEmpty) errors.username = t("login.pleaseEnterUsername");
      if (passwordEmpty) errors.password = t("login.pleaseEnterPassword");
      setFieldErrors(errors);
      const firstMessage = errors.username || errors.password;
      if (firstMessage) notifyError(firstMessage);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    try {
      const response = await authApi.login({
        username: form.username.trim(),
        password: form.password,
      });

      if (onLoginSuccess) {
        onLoginSuccess(response);
        return;
      }

      setUserFromToken(response, true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      notifySuccess(t("login.loginSuccess"));
      navigate("/dashboard");
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        t("login.invalidCredentials");
      setFieldErrors({
        username: t("login.invalidUsername"),
        password: t("login.invalidPassword"),
      });
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles["user-login"]}>
      {formFields.map(
        ({ label, name, placeholder, type, variant, startIcon, maxLength }) => {
          const errorMessage = fieldErrors[name as keyof FieldErrors];
          return (
            <CustomInput
              key={name}
              error={!!errorMessage}
              errorMessage={errorMessage}
              label={label}
              onChange={handleInputChange(name as "username" | "password")}
              placeholder={placeholder}
              type={type}
              maxLength={maxLength}
              value={form[name as keyof typeof form]}
              variant={variant}
              startIcon={startIcon}
            />
          );
        },
      )}

      <div className={styles["login-options"]}>
        <></>
        {/* {onForgotPassword && (
          <button
            type="button"
            onClick={onForgotPassword}
            className={styles["forgot-password-link"]}
          >
            {t("login.forgotPassword")}
          </button>
        )} */}
      </div>

      <Button
        color="primary"
        type="submit"
        variant="contained"
        loading={isLoading}
      >
        {t("buttons.login")}
      </Button>
    </form>
  );
};

export default AdminLogin;
